import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "./Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  isDedicatedLabArea: "",
  infrastructureChallenges: "",
  isVentilated: "",
  ventilationComment: "",
  infrastructureChallenges2: ["", ""],
  waterSourceChallenges: "",
  energySources: ["", ""],
  energySourceChallenges: "",
  hasStaffToilets: "",
  staffToiletsComment: "",
  hasPatientToilets: "",
  patientToiletsComment: "",
  hasAircons: "",
  airconComment: "",
  officeSpaceAvailability: {
    labIncharge: "",
    safetyOfficer: "",
    qualityOfficer: "",
    logisticsOfficer: "",
  },
  officeSpaceComment: "",
  storageFacilities: {
    reagentStorageComment: "",
    hasShelves: "",
    hasRefrigerators: "",
    refrigeratorsComment: "",
    hasThermometers: "",
    thermometersComment: "",
  },
};

const Infrastructure = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/infrastructure`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: "infrastructure",
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/infrastructure`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
      setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    }finally{
      setLoading(false);
    }
  };

  const questionSets = [
    {
      title: "Laboratory infrastructure",
      subtitle: "A. Laboratory testing areas",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there an area dedicated specifically to the Laboratory?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="isDedicatedLabArea"
                checked={formData.isDedicatedLabArea === "Yes"}
                onChange={() => handleChange("isDedicatedLabArea", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isDedicatedLabArea"
                checked={formData.isDedicatedLabArea === "No"}
                onChange={() => handleChange("isDedicatedLabArea", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={2}
            question="Is there enough Ventilation?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.ventilationComment}
            onChange={(e) => handleChange("ventilationComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="isVentilated"
                checked={formData.isVentilated === "Yes"}
                onChange={() => handleChange("isVentilated", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isVentilated"
                checked={formData.isVentilated === "No"}
                onChange={() => handleChange("isVentilated", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Challenges with infrastructure"
            singleInput
            answers={formData.infrastructureChallenges2}
            onAnswersChange={(newAnswers) =>
              handleChange("infrastructureChallenges2", newAnswers)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={4}
            question="Challenges with water source"
            multilineInput={true}
            value={formData.waterSourceChallenges}
            onChange={(e) =>
              handleChange("waterSourceChallenges", e.target.value)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={5}
            question="List the energy source is the Laboratory using?"
            singleInput={true}
            answers={formData.energySources}
            onAnswersChange={(newAnswers) =>
              handleChange("energySources", newAnswers)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={6}
            question="Challenges with energy source"
            multilineInput={true}
            value={formData.energySourceChallenges}
            onChange={(e) =>
              handleChange("energySourceChallenges", e.target.value)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={7}
            question="Availability of Staff Toilets"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.staffToiletsComment}
            onChange={(e) =>
              handleChange("staffToiletsComment", e.target.value)
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasStaffToilets"
                checked={formData.hasStaffToilets === "Yes"}
                onChange={() => handleChange("hasStaffToilets", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasStaffToilets"
                checked={formData.hasStaffToilets === "No"}
                onChange={() => handleChange("hasStaffToilets", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={8}
            question="Availability of Patient Toilets"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.patientToiletsComment}
            onChange={(e) =>
              handleChange("patientToiletsComment", e.target.value)
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasPatientToilets"
                checked={formData.hasPatientToilets === "Yes"}
                onChange={() => handleChange("hasPatientToilets", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasPatientToilets"
                checked={formData.hasPatientToilets === "No"}
                onChange={() => handleChange("hasPatientToilets", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={9}
            question="Availability of Aircons"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.airconComment}
            onChange={(e) => handleChange("airconComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasAircons"
                checked={formData.hasAircons === "Yes"}
                onChange={() => handleChange("hasAircons", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasAircons"
                checked={formData.hasAircons === "No"}
                onChange={() => handleChange("hasAircons", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={10}
            question="Availability of Office Space for"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.officeSpaceComment}
            onChange={(e) => handleChange("officeSpaceComment", e.target.value)}
            disabled={isReadOnly}
          >
            <div className="d-flex flex-column gap-2 w-75 mb-4">
              {[
                "labIncharge",
                "safetyOfficer",
                "qualityOfficer",
                "logisticsOfficer",
              ].map((role) => (
                <div className="d-flex gap-2" key={role}>
                  <QuestionCard
                    question={
                      <span className={classes.questionText}>
                        {role.replace(/([A-Z])/g, " $1")}
                      </span>
                    }
                  />
                  <RadioButton
                    label="Yes"
                    name={role}
                    checked={formData.officeSpaceAvailability[role] === "Yes"}
                    onChange={() =>
                      handleNestedChange("officeSpaceAvailability", role, "Yes")
                    }
                    disabled={isReadOnly}
                  />
                  <RadioButton
                    label="No"
                    name={role}
                    checked={formData.officeSpaceAvailability[role] === "No"}
                    onChange={() =>
                      handleNestedChange("officeSpaceAvailability", role, "No")
                    }
                    disabled={isReadOnly}
                  />
                </div>
              ))}
            </div>
          </QuestionBox>
        </div>
      ),
    },
    {
      title: "Laboratory infrastructure",
      subtitle: "B. Storage facilities",
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="Is there Area dedicated for reagent storage?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.reagentStorageComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "reagentStorageComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          ></QuestionBox>
          <QuestionBox
            number={2}
            question="Availability of Shelves in the storage Area?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-4">
              <RadioButton
                label="Yes"
                name="hasShelves"
                checked={formData.storageFacilities.hasShelves === "Yes"}
                onChange={() =>
                  handleNestedChange("storageFacilities", "hasShelves", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasShelves"
                checked={formData.storageFacilities.hasShelves === "No"}
                onChange={() =>
                  handleNestedChange("storageFacilities", "hasShelves", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={3}
            question="Availability of Refrigerators in the store room?"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.refrigeratorsComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "refrigeratorsComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasRefrigerators"
                checked={formData.storageFacilities.hasRefrigerators === "Yes"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasRefrigerators",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasRefrigerators"
                checked={formData.storageFacilities.hasRefrigerators === "No"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasRefrigerators",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Availability of thermometers"
            multilineInput={true}
            placeholder="Add comment"
            value={formData.storageFacilities.thermometersComment}
            onChange={(e) =>
              handleNestedChange(
                "storageFacilities",
                "thermometersComment",
                e.target.value
              )
            }
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasThermometers"
                checked={formData.storageFacilities.hasThermometers === "Yes"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasThermometers",
                    "Yes"
                  )
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasThermometers"
                checked={formData.storageFacilities.hasThermometers === "No"}
                onChange={() =>
                  handleNestedChange(
                    "storageFacilities",
                    "hasThermometers",
                    "No"
                  )
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Laboratory infrastructure</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      <h4>{subtitle}</h4>
     {(!editMode && userRole !== "MINISTRY") ? (
        <div className={classes.infoText}>
          ✅ Your laboratory infrastructure has been submitted.
        </div>
      ) : (
        content
      )}

      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          disabled={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default Infrastructure;
